import { useCallback, useState } from 'react';
import {
  fetchAuthSession,
  getCurrentUser,
  confirmResetPassword,
  confirmSignIn,
  resendSignUpCode,
  resetPassword as resetPasswordAmplify,
  signIn,
  SignInOutput,
  updatePassword as updatePasswordAmplify,
  setUpTOTP,
  verifyTOTPSetup,
  signOut,
  fetchMFAPreference,
  sendUserAttributeVerificationCode,
  updateMFAPreference,
} from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@cp/core/routing/constants';
import { withAuthRegionFailover } from '@/auth';
import { getUrl } from '@/utils/helpers/url.helpers';

type TotpDetails = {
  uri: string;
  secret: string;
};

type LoginInput = {
  email: string;
  password: string;
};

type UpdatePasswordInput = {
  newPassword: string;
};

type ConfirmCodeInput = {
  code: string;
};

type UpdatePhoneInput = {
  phone: string;
};

type ProfileType = {
  userId: string;
  userName: string;
};

type NavigationRoutes = {
  SIGN_IN: {
    ROOT: string;
    TOTP_CODE: string;
  };
};

export type MfaType = 'SMS' | 'TOTP';

export const MFA_TYPES = {
  SMS: 'SMS' as const,
  TOTP: 'TOTP' as const,
};

type MfaState = {
  availableMfaTypes: MfaType[];
  selectedMfaType: MfaType | null;
  codeDeliveryDetails?: {
    destination?: string;
    deliveryMedium?: string;
  };
};

export type AmplifyAuthData = {
  clientProfile: ProfileType | null;
  email: string;
  error: string;
  isOtpVerified: boolean;
  totpDetails: TotpDetails | null;
  mfaState: MfaState;
  getClientProfileToken: () => Promise<string | null>;
  resetClientProfile: () => void;
  setEmail: (email: string) => void;
  setClientSession: () => Promise<void>;
  login: (input: LoginInput, keepLoggedIn?: boolean) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  updatePassword: (input: UpdatePasswordInput) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  resetPassword: (newPassword: string, confirmationCode: string) => Promise<void>;
  resendSmsCode: () => Promise<void>;
  confirmCode: (input: ConfirmCodeInput) => Promise<void>;
  updatePhone: (input: UpdatePhoneInput) => Promise<void>;
  setupTOTP: () => Promise<void>;
  verifyTOTP: (code: string) => Promise<boolean>;
  getMfaPreferences: () => Promise<MfaType[]>;
  initMfaPreferences: () => Promise<void>;
  selectMfaMethod: (mfaType: MfaType) => Promise<void>;
  sendSmsVerificationCode: () => Promise<void>;
  clearError: () => void;
  signOut: () => Promise<void>;
};

export type PublicAmplifyAuthData = Omit<
  AmplifyAuthData,
  'clientProfile' | 'getClientProfileToken' | 'resetClientProfile' | 'setClientSession' | 'signOut' | 'clearError'
>;

export const amplifyAuthDefaultValues: PublicAmplifyAuthData = {
  email: '',
  error: '',
  isOtpVerified: false,
  totpDetails: null,
  mfaState: {
    availableMfaTypes: [],
    selectedMfaType: null,
  },
  setEmail: () => {},
  login: () => Promise.resolve(),
  forgotPassword: () => Promise.resolve(),
  updatePassword: () => Promise.resolve(),
  changePassword: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
  resendSmsCode: () => Promise.resolve(),
  confirmCode: () => Promise.resolve(),
  updatePhone: () => Promise.resolve(),
  setupTOTP: () => Promise.resolve(),
  verifyTOTP: () => Promise.resolve(false),
  getMfaPreferences: () => Promise.resolve([]),
  initMfaPreferences: () => Promise.resolve(),
  selectMfaMethod: () => Promise.resolve(),
  sendSmsVerificationCode: () => Promise.resolve(),
};

export const useAmplifyAuth = <T extends NavigationRoutes>(routes: T): AmplifyAuthData => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [totpDetails, setTotpDetails] = useState<TotpDetails | null>(null);

  const [mfaState, setMfaState] = useState<MfaState>({
    availableMfaTypes: [],
    selectedMfaType: null,
  });

  const getClientProfileToken = useCallback(async (): Promise<string | null> => {
    const authSession = await withAuthRegionFailover(() => fetchAuthSession());
    return authSession.tokens?.accessToken.toString() ?? null;
  }, []);

  const setClientSession = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();

      if (currentUser?.userId && currentUser?.username) {
        setError('');
        setProfile({
          userId: currentUser.userId,
          userName: currentUser.username,
        });
      } else {
        setError('User not found or invalid user data.');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      console.error('❌ Session setup error during login:', msg, e);
      setError(`Error fetching user: ${msg}`);
    }
  }, []);

  const handleAuthOutput = useCallback(
    async (authOutput: SignInOutput) => {
      const { nextStep, isSignedIn } = authOutput;

      setMfaState((prev) => ({ ...prev, codeDeliveryDetails: undefined }));

      switch (nextStep.signInStep) {
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          navigate(getUrl([ROUTES.SIGN_IN.ROOT, ROUTES.SIGN_IN.NEW_PASSWORD]));
          break;

        case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
          setTotpDetails({
            uri: nextStep.totpSetupDetails.getSetupUri('TMDX', email).href,
            secret: nextStep.totpSetupDetails.sharedSecret,
          });
          setMfaState((prev) => ({
            ...prev,
            selectedMfaType: MFA_TYPES.TOTP,
          }));
          navigate(getUrl([ROUTES.SIGN_IN.ROOT, ROUTES.SIGN_IN.TOTP_SETUP]));
          break;

        case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION': {
          const authMfaTypes = nextStep.allowedMFATypes as MfaType[];
          setMfaState((prev) => ({
            ...prev,
            availableMfaTypes: authMfaTypes,
            selectedMfaType: null,
          }));
          navigate(getUrl([ROUTES.SIGN_IN.ROOT, ROUTES.SIGN_IN.MFA]));
          break;
        }

        case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
          setMfaState((prev) => ({
            ...prev,
            selectedMfaType: MFA_TYPES.TOTP,
          }));
          navigate(getUrl([ROUTES.SIGN_IN.ROOT, ROUTES.SIGN_IN.TOTP_CODE]));
          break;

        case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
          setMfaState((prev) => ({
            ...prev,
            selectedMfaType: MFA_TYPES.SMS,
            codeDeliveryDetails: nextStep.codeDeliveryDetails,
          }));
          navigate(getUrl([ROUTES.SIGN_IN.ROOT, ROUTES.SIGN_IN.SMS_CODE]));
          break;

        case 'DONE':
          if (mfaState.availableMfaTypes.length > 0) {
            const hasSms = mfaState.availableMfaTypes.includes(MFA_TYPES.SMS);
            const hasTotp = mfaState.availableMfaTypes.includes(MFA_TYPES.TOTP);

            try {
              const updatePayload = {
                ...(hasSms && { sms: 'ENABLED' as const }),
                ...(hasTotp && { totp: 'ENABLED' as const }),
              };
              await updateMFAPreference(updatePayload);
            } catch (e) {
              console.error('❌ Failed to sync MFA preferences:', e);
            }
          } else {
            console.log('⚠️ No availableMfaTypes to sync');
          }

          setMfaState((prev) => ({
            ...prev,
            selectedMfaType: null,
          }));
          break;

        default:
          console.warn('Unhandled auth step:', nextStep.signInStep);
      }

      return isSignedIn;
    },
    [email, navigate, mfaState],
  );

  const selectMfaMethod = useCallback(
    async (mfaType: MfaType, confirmAuth: boolean = true) => {
      try {
        setError('');
        setMfaState((prev) => ({ ...prev, selectedMfaType: mfaType }));

        if (confirmAuth) {
          const authOutput = await confirmSignIn({ challengeResponse: mfaType });
          await handleAuthOutput(authOutput);
        }
      } catch (e) {
        const authError = e as Error;
        console.error('❌ MFA selection error:', authError.message);
        setError(authError.message);
      }
    },
    [handleAuthOutput],
  );

  const login = useCallback(
    async (input: LoginInput) => {
      try {
        setError('');
        setEmail(input.email);
        setPassword(input.password);

        setMfaState({
          availableMfaTypes: [],
          selectedMfaType: null,
        });

        // Only the flow-starting call is wrapped. confirmSignIn and the other
        // challenge continuations must not be: the Session that InitiateAuth returns
        // belongs to the Region that issued it.
        const authOutput = await withAuthRegionFailover(() =>
          signIn({ username: input.email, password: input.password }),
        );
        await handleAuthOutput(authOutput);
      } catch (e: unknown) {
        const authError = e as Error;
        console.error('❌ Login error:', authError.message);
        setError(authError.message);
      }
    },
    [handleAuthOutput],
  );

  const forgotPassword = useCallback(async (userEmail: string) => {
    try {
      if (!userEmail.length) {
        setError('Email field is required');
        return;
      }
      setError('');
      setEmail(userEmail);
      await resetPasswordAmplify({ username: userEmail });
    } catch (e: unknown) {
      const authError = e as Error;
      setError(authError.message);
    }
  }, []);

  const resetPassword = useCallback(
    async (newPassword: string, confirmationCode: string) => {
      try {
        setError('');
        await confirmResetPassword({
          username: email,
          newPassword,
          confirmationCode,
        });
        navigate(routes.SIGN_IN.ROOT);
      } catch (e: unknown) {
        const authError = e as Error;
        setError(authError.message);
      }
    },
    [email, navigate, routes.SIGN_IN.ROOT],
  );

  const updatePassword = useCallback(
    async (input: UpdatePasswordInput) => {
      try {
        setError('');
        const authOutput = await confirmSignIn({ challengeResponse: input.newPassword });
        await handleAuthOutput(authOutput);
      } catch (e) {
        const authError = e as Error;
        setError(authError.message);
      }
    },
    [handleAuthOutput],
  );

  const changePassword = useCallback(async (oldPassword: string, newPassword: string) => {
    try {
      setError('');
      await updatePasswordAmplify({
        oldPassword,
        newPassword,
      });
    } catch (e) {
      const authError = e as Error;
      setError(authError.message);
      throw e;
    }
  }, []);

  const resendSmsCode = useCallback(async () => {
    try {
      setError('');

      if (mfaState.selectedMfaType === MFA_TYPES.SMS) {
        // For MFA during sign-in, restart the sign-in flow to get a new SMS code
        // This is the only way to resend MFA SMS codes in AWS Cognito
        if (!email || !password) {
          setError('Unable to resend code. Please sign in again.');
          return;
        }

        // Restart the sign-in flow which will send a new SMS code
        const output = await signIn({ username: email, password });

        // If MFA selection is required, automatically select SMS
        if (output.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
          const smsOutput = await confirmSignIn({ challengeResponse: MFA_TYPES.SMS });
          await handleAuthOutput(smsOutput);
        } else {
          await handleAuthOutput(output);
        }
      } else {
        // For sign-up verification, use resendSignUpCode
        await resendSignUpCode({ username: email });
      }
    } catch (e: unknown) {
      const authError = e as Error;
      setError(authError.message);
    }
  }, [email, password, mfaState.selectedMfaType, handleAuthOutput]);

  const sendSmsVerificationCode = useCallback(async () => {
    try {
      setError('');
      await sendUserAttributeVerificationCode({
        userAttributeKey: 'phone_number',
      });
    } catch (e: unknown) {
      const verifyUserAttributeError = e as Error;
      console.error('❌ Send user attribute phone number error:', verifyUserAttributeError.message);
      setError(verifyUserAttributeError.message);
    }
  }, []);

  const confirmCode = useCallback(
    async (input: ConfirmCodeInput) => {
      try {
        setError('');
        const authOutput = await confirmSignIn({ challengeResponse: input.code });
        await handleAuthOutput(authOutput);
      } catch (e: unknown) {
        const authError = e as Error;
        setError(authError.message);
      }
    },
    [handleAuthOutput],
  );

  const updatePhone = useCallback(
    async (input: UpdatePhoneInput) => {
      try {
        setError('');
        const authOutput = await signIn({
          username: email,
          password,
          options: {
            userAttributes: {
              phone_number: input.phone,
            },
          },
        });
        await handleAuthOutput(authOutput);
      } catch (e: unknown) {
        const authError = e as Error;
        setError(authError.message);
      }
    },
    [email, password, handleAuthOutput],
  );

  const setupTOTP = useCallback(async () => {
    try {
      setError('');

      const totpSetupDetails = await setUpTOTP();

      setTotpDetails({
        uri: totpSetupDetails.getSetupUri('TMDX', email).href,
        secret: totpSetupDetails.sharedSecret,
      });
    } catch (e) {
      const authError = e as Error;
      console.error('❌ TOTP setup error:', authError.message);
      setError(authError.message);
    }
  }, [email]);

  const verifyTOTP = useCallback(async (code: string) => {
    try {
      setError('');

      if (!code || code.trim().length === 0) {
        setError('Verification code is required');
        return false;
      }

      if (code.trim().length !== 6) {
        setError('Verification code must be 6 digits');
        return false;
      }

      await verifyTOTPSetup({ code: code.trim() });

      const currentPreferences = await fetchMFAPreference();
      const isSmsEnabled = currentPreferences.enabled?.includes('SMS');

      await updateMFAPreference({
        totp: 'ENABLED',
        ...(isSmsEnabled && { sms: 'ENABLED' }),
      });

      setMfaState((prev) => {
        const hasTotp = prev.availableMfaTypes.includes(MFA_TYPES.TOTP);
        return {
          ...prev,
          availableMfaTypes: hasTotp ? prev.availableMfaTypes : [...prev.availableMfaTypes, MFA_TYPES.TOTP],
        };
      });

      setIsOtpVerified(true);
      return true;
    } catch (e) {
      const authError = e as Error;
      setError(authError.message);
      return false;
    }
  }, []);

  const getMfaPreferences = useCallback(async (): Promise<MfaType[]> => {
    try {
      setError('');
      const response = await fetchMFAPreference();
      const mfaTypes = (response.enabled as MfaType[]) ?? [];
      setMfaState((prev) => ({
        ...prev,
        availableMfaTypes: mfaTypes,
      }));

      return mfaTypes;
    } catch (e) {
      const authError = e as Error;
      console.error('❌ MFA preferences retrieval error:', authError.message);
      setError(authError.message);
      return [];
    }
  }, []);

  const initMfaPreferences = useCallback(async () => {
    try {
      setError('');
      await getMfaPreferences();
    } catch (e) {
      const authError = e as Error;
      console.error('❌ Init MFA preference error:', authError.message);
      setError(authError.message);
    }
  }, [getMfaPreferences]);

  const resetClientProfile = () => {
    setProfile(null);
    setMfaState({
      availableMfaTypes: [],
      selectedMfaType: null,
    });
  };

  const clearError = useCallback(() => {
    setError('');
  }, []);

  return {
    clientProfile: profile,
    email,
    error,
    isOtpVerified,
    totpDetails,
    mfaState,
    resetClientProfile,
    setEmail,
    getClientProfileToken,
    setClientSession,
    login,
    forgotPassword,
    updatePassword,
    changePassword,
    resetPassword,
    resendSmsCode,
    confirmCode,
    updatePhone,
    setupTOTP,
    verifyTOTP,
    getMfaPreferences,
    initMfaPreferences,
    selectMfaMethod,
    sendSmsVerificationCode,
    clearError,
    signOut,
  };
};
